import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { copyToClipboard } from '@digilize/shared/utils/helpers';

export interface DialogGtmSnippetData {
  /** Already normalized and regex-checked by the caller; null when the field is empty or invalid. */
  containerId: string | null;
}

export type GtmSnippetBlock = 'head' | 'footer';

/* Google's own install snippets, kept verbatim - including their line breaks - so the blocks can be
   compared against Google's documentation line for line. Only the container id is interpolated. */
const headSnippetFor = (containerId: string) => `<!-- Google Tag Manager -->
<script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${containerId}');</script>
<!-- End Google Tag Manager -->`;

const footerSnippetFor = (containerId: string) => `<!-- Google Tag Manager (noscript) -->
<noscript><iframe src="https://www.googletagmanager.com/ns.html?id=${containerId}"
height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>
<!-- End Google Tag Manager (noscript) -->`;

@Component({
  selector: 'lib-dialog-gtm-snippet',
  templateUrl: './dialog-gtm-snippet.component.html',
  styleUrls: ['./dialog-gtm-snippet.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DialogGtmSnippetComponent {
  /* Both stay null without a container id: a snippet carrying an empty id looks installable but
     loads nothing, so the dialog shows the empty state instead. */
  readonly headSnippet: string | null = null;
  readonly footerSnippet: string | null = null;

  /* Which block was copied last. A snackbar is not reachable from here - SnackbarService lives in a
     `type:feature` lib and this component is `type:ui` - so the confirmation sits on the button. */
  copied: GtmSnippetBlock | null = null;

  constructor(
    public dialogRef: MatDialogRef<DialogGtmSnippetComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogGtmSnippetData
  ) {
    if (data.containerId) {
      this.headSnippet = headSnippetFor(data.containerId);
      this.footerSnippet = footerSnippetFor(data.containerId);
    }
  }

  copy(block: GtmSnippetBlock) {
    copyToClipboard(block === 'head' ? this.headSnippet : this.footerSnippet);
    this.copied = block;
  }

  close() {
    this.dialogRef.close();
  }
}
